import {Injectable} from '@angular/core';
import {bindCallback, ConnectableObservable, Observable, of, ReplaySubject, throwError, from} from 'rxjs';
import {map, multicast, switchMap} from 'rxjs/operators';
import {ExgMapAPILoader} from './exg-map-api-loader.service';

@Injectable({providedIn: 'root'})
export class ExgMapPlacesServices {
  protected readonly autocompleteservice$: Observable<google.maps.places.AutocompleteSuggestion>;
  private _sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

  constructor(loader: ExgMapAPILoader) {
    const connectableAutoCompleteService$ = new Observable(subscriber => {
      loader.load().then(() => subscriber.next());
    })
      .pipe(
        map(() => this._createAutoCompleteService()),
        multicast(new ReplaySubject(1)),
      ) as ConnectableObservable<google.maps.places.AutocompleteSuggestion>;

    connectableAutoCompleteService$.connect(); // ignore the subscription
    // since we will remain subscribed till application exits

    this.autocompleteservice$ = connectableAutoCompleteService$;
  }

  autocomplete(request: google.maps.places.AutocompleteRequest): Observable<google.maps.places.AutocompleteSuggestion[]> {
    if (this._sessionToken === undefined) {
      this._sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
    if (request.sessionToken === undefined) {
      request.sessionToken = this._sessionToken;
    }
    return this.autocompleteservice$.pipe(
      switchMap((autocompletionsuggestion) => this._getGoogleAutoCompletionResults(autocompletionsuggestion, request))
    );
  }

  toPlace(autocompletionsuggestion: google.maps.places.AutocompleteSuggestion, fields?: string[]): Promise<{
    place: google.maps.places.Place | undefined,
    exgPlace: IExgPlace
  }> | undefined {
    const _fields = fields || ['displayName', 'addressComponents', 'adrFormatAddress', 'formattedAddress', 'location', 'plusCode', 'types', 'viewport'];
    let pl = autocompletionsuggestion.placePrediction?.toPlace();

    const exgPl: IExgPlace = {
      name: undefined,
      formatted: undefined,
      html: undefined,
      street: undefined,
      street_number: undefined,
      zip: undefined,
      city: undefined,
      country: undefined,
      lat: undefined,
      lng: undefined
    };
    const p = new Promise<{
      place: google.maps.places.Place | undefined,
      exgPlace: IExgPlace
    }>((resolve) => {
      pl?.fetchFields({fields: _fields})
        .then(value => {
          pl = value.place;
          if (pl.addressComponents) {
            for (const v of pl.addressComponents) {
              if (v.types.findIndex(value1 => value1 === 'street_number') > -1) {
                exgPl.street_number = v.longText as any;
              }
              if (v.types.findIndex(value1 => value1 === 'route') > -1) {
                exgPl.street = v.longText as any;
              }
              if (v.types.findIndex(value1 => value1 === 'locality') > -1) {
                exgPl.city = v.longText as any;
              }
              if (v.types.findIndex(value1 => value1 === 'postal_code') > -1) {
                exgPl.zip = v.longText as any;
              }
              if (v.types.findIndex(value1 => value1 === 'country') > -1) {
                exgPl.country = v.longText as any;
              }
            }
            if (pl.location) {
              exgPl.lat = pl.location.lat();
              exgPl.lng = pl.location.lng();
            }
            exgPl.name = pl.displayName as any;
            exgPl.formatted = pl.formattedAddress as any;
            exgPl.html = pl.adrFormatAddress as any;
          }
          resolve({place: pl, exgPlace: exgPl});
        });

    });
    return p;
  }

  private _getGoogleAutoCompletionResults(
    autocompletionservice: google.maps.places.AutocompleteSuggestion,
    request: google.maps.places.AutocompleteRequest):
    Observable<google.maps.places.AutocompleteSuggestion[]> {
    const autocompleteObservable = from(google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request));
    return autocompleteObservable.pipe(switchMap(result => {
      return of(result.suggestions);
    }));
    /*.pipe(
      switchMap(([results]) => {
          return of(results);
      })*/
    // );
  }

  private _createAutoCompleteService(): google.maps.places.AutocompleteSuggestion {
    return new google.maps.places.AutocompleteSuggestion();
  }
}

interface IExgPlace {
  name?: string;
  formatted?: string;
  html?: string;
  street?: string;
  street_number?: string;
  zip?: string;
  city?: string;
  country?: string;
  lat?: number;
  lng?: number;

  [key: string]: any;
}
